import { useSyncExternalStore } from 'react'

import { MOCK_ENABLED } from '@lib/constants'

export type MockConfig = {
  enabled: boolean
  forcePrism: boolean
  count: number | null
  seed: string | null
  now: string | null
  code: number | null
  delay: number | null
}

export const STORAGE_KEY = 'admin-mock-mode'

export const MOCK_PARAMS = {
  forcePrism: '_force_prism',
  count: '_mock_count',
  seed: '_mock_seed',
  now: '_mock_now',
  code: '_mock_code',
  delay: '_mock_delay',
} as const

export const MOCK_DELAY_MAX = 10000

export const DEFAULT_MOCK_CONFIG: MockConfig = {
  enabled: false,
  forcePrism: false,
  count: null,
  seed: null,
  now: null,
  code: null,
  delay: null,
}

const readInitial = (): MockConfig => {
  try {
    const raw = typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY)

    return raw ? { ...DEFAULT_MOCK_CONFIG, ...JSON.parse(raw) } : DEFAULT_MOCK_CONFIG
  } catch {
    return DEFAULT_MOCK_CONFIG
  }
}

let mockConfig = readInitial()
const listeners = new Set<() => void>()

export const getMockConfig = (): MockConfig => mockConfig

// Production kill-switch: never active unless the build flag is on.
export const isMockModeActive = (): boolean => MOCK_ENABLED && mockConfig.enabled

export const setMockConfig = (partial: Partial<MockConfig>): void => {
  mockConfig = { ...mockConfig, ...partial }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(mockConfig))
  } catch {
    /* ignore quota / SSR */
  }
  listeners.forEach((l) => l())
}

export const subscribeMockMode = (listener: () => void): (() => void) => {
  listeners.add(listener)

  return () => listeners.delete(listener)
}

export const useMockMode = () => ({
  config: useSyncExternalStore(subscribeMockMode, getMockConfig, () => DEFAULT_MOCK_CONFIG),
  setConfig: setMockConfig,
})

// Mock controls as URL query params; _force_prism is implied by any generator/code control.
export const buildMockParams = ({ enabled, forcePrism, count, seed, now, code, delay }: MockConfig) => {
  if (!enabled) return {}

  const params: Record<string, string> = {}
  if (forcePrism || count !== null || seed !== null || now !== null || code !== null) {
    params[MOCK_PARAMS.forcePrism] = 'true'
  }
  if (count !== null) params[MOCK_PARAMS.count] = String(count)
  if (seed !== null) params[MOCK_PARAMS.seed] = seed
  if (now !== null) params[MOCK_PARAMS.now] = now
  if (code !== null) params[MOCK_PARAMS.code] = String(code)
  if (delay !== null) params[MOCK_PARAMS.delay] = String(Math.min(Math.max(delay, 0), MOCK_DELAY_MAX))

  return params
}
